/**
 * Page sizes and print geometry for HTML designs.
 *
 * Design coordinates are CSS pixels (1px = 1/96in), so the editor canvas, the
 * serialized HTML and the printed page all share one coordinate space.
 */

export const CSS_DPI = 96;
export const MM_PER_INCH = 25.4;

/** @typedef {"a4-landscape"|"a4-portrait"|"letter-landscape"|"letter-portrait"|"cr80-landscape"|"cr80-portrait"} SizeId */

export const SIZE_PRESETS = Object.freeze({
  "a4-landscape": {
    labelKey: "size_a4_landscape",
    widthMm: 297,
    heightMm: 210,
    kind: "certificate",
  },
  "a4-portrait": {
    labelKey: "size_a4_portrait",
    widthMm: 210,
    heightMm: 297,
    kind: "certificate",
  },
  "letter-landscape": {
    labelKey: "size_letter_landscape",
    widthMm: 279.4,
    heightMm: 215.9,
    kind: "certificate",
  },
  "letter-portrait": {
    labelKey: "size_letter_portrait",
    widthMm: 215.9,
    heightMm: 279.4,
    kind: "certificate",
  },
  "cr80-landscape": {
    labelKey: "size_cr80_landscape",
    widthMm: 85.6,
    heightMm: 53.98,
    kind: "card",
  },
  "cr80-portrait": {
    labelKey: "size_cr80_portrait",
    widthMm: 53.98,
    heightMm: 85.6,
    kind: "card",
  },
});

export const DEFAULT_SIZE_ID = "a4-landscape";

export const DEFAULT_PRINT = Object.freeze({
  bleedMm: 0,
  safeMarginMm: 8,
  colorProfile: "srgb",
});

export const mmToCssPx = (mm) => Math.round((mm / MM_PER_INCH) * CSS_DPI);
export const mmToPx = (mm, dpi) => Math.round((mm / MM_PER_INCH) * dpi);

/**
 * @param {SizeId} sizeId
 * @returns {{ id: SizeId, widthMm: number, heightMm: number, widthPx: number, heightPx: number, dpi: number }}
 */
export function createSize(sizeId = DEFAULT_SIZE_ID) {
  const preset = SIZE_PRESETS[sizeId] || SIZE_PRESETS[DEFAULT_SIZE_ID];
  const id = SIZE_PRESETS[sizeId] ? sizeId : DEFAULT_SIZE_ID;
  return {
    id,
    widthMm: preset.widthMm,
    heightMm: preset.heightMm,
    widthPx: mmToCssPx(preset.widthMm),
    heightPx: mmToCssPx(preset.heightMm),
    dpi: CSS_DPI,
  };
}

/** Safe margin shrinks on small formats so ID cards keep a usable area. */
export function getEffectiveSafeMarginMm(size, print = DEFAULT_PRINT) {
  const shortSide = Math.min(size.widthMm, size.heightMm);
  return Math.min(
    print.safeMarginMm ?? DEFAULT_PRINT.safeMarginMm,
    shortSide / 12,
  );
}

/** Trim and safe-area rectangles in design (CSS px) coordinates. */
export function getGuideRects(size, print = DEFAULT_PRINT) {
  const safe = mmToCssPx(getEffectiveSafeMarginMm(size, print));
  return {
    trim: { x: 0, y: 0, width: size.widthPx, height: size.heightPx },
    safe: {
      x: safe,
      y: safe,
      width: size.widthPx - safe * 2,
      height: size.heightPx - safe * 2,
    },
  };
}

/**
 * Scale every element when the page size changes, keeping relative layout.
 * Uniform scale (min of both axes) keeps text and images undistorted; content
 * is centred on the axis with slack.
 */
export function rescaleElementsForSize(elements, fromSize, toSize) {
  const scale = Math.min(
    toSize.widthPx / fromSize.widthPx,
    toSize.heightPx / fromSize.heightPx,
  );
  const offsetX = (toSize.widthPx - fromSize.widthPx * scale) / 2;
  const offsetY = (toSize.heightPx - fromSize.heightPx * scale) / 2;
  const round = (value) => Math.round(value * 100) / 100;

  return elements.map((element) => {
    const next = {
      ...element,
      x: round(element.x * scale + offsetX),
      y: round(element.y * scale + offsetY),
      width: round(element.width * scale),
      height: round(element.height * scale),
    };
    if (element.type === "text") {
      next.fontSize = round(element.fontSize * scale);
      next.letterSpacing = round((element.letterSpacing || 0) * scale);
    }
    if (element.type === "shape") {
      next.strokeWidth = round((element.strokeWidth || 0) * scale);
      next.cornerRadius = round((element.cornerRadius || 0) * scale);
    }
    if (element.type === "image") {
      next.borderRadius = round((element.borderRadius || 0) * scale);
    }
    return next;
  });
}
